import DoneAllOutlined from '@mui/icons-material/DoneAllOutlined';
import DoneOutlined from '@mui/icons-material/DoneOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { MessagingChannelType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { ReactNode, useState } from 'react';

import { MessagingChannel, MessagingMessage } from '~hooks/useMessaging/models';
import MessageReadersList from '~pages/Messages/components/MessageReadersList';

// Sized to the caption text it sits beside.
const RECEIPT_ICON_SIZE_IN_PX = 14;

type ReceiptIconProps = {
  icon: ReactNode;
  label: string;
  testId: string;
};

function ReceiptIcon({ icon, label, testId }: ReceiptIconProps) {
  return (
    <Tooltip title={label}>
      <Box
        aria-label={label}
        component="span"
        data-test-id={testId}
        role="img"
        sx={{ display: 'inline-flex' }}
      >
        {icon}
      </Box>
    </Tooltip>
  );
}

const channelTypeListsReaders = {
  [MessagingChannelType.BROADCAST]: true,
  [MessagingChannelType.DIRECT]: false,
  [MessagingChannelType.JOB]: true,
  [MessagingChannelType.ORDER]: true,
} satisfies Record<MessagingChannelType, boolean>;

type MessageReadReceiptProps = {
  channel: MessagingChannel;
  message: MessagingMessage;
};

export default function MessageReadReceipt({
  channel,
  message,
}: MessageReadReceiptProps) {
  const theme = useTheme();
  const [readersAnchor, setReadersAnchor] = useState<HTMLElement | null>(null);

  const isRead = message.readCount > 0;
  const listsReaders = channelTypeListsReaders[channel.channelType];

  if (!isRead) {
    return (
      <ReceiptIcon
        icon={<DoneOutlined sx={{ fontSize: RECEIPT_ICON_SIZE_IN_PX }} />}
        label={t('messaging.receipt_sent')}
        testId="messaging-message-receipt-sent"
      />
    );
  }

  const readIconSx = { fontSize: RECEIPT_ICON_SIZE_IN_PX };
  const readByEveryoneIconSx = { ...readIconSx, color: theme.semantic.linkText };

  if (!listsReaders) {
    return (
      <ReceiptIcon
        icon={<DoneAllOutlined sx={readByEveryoneIconSx} />}
        label={t('messaging.receipt_read')}
        testId="messaging-message-receipt-read"
      />
    );
  }

  const possibleReaderCount = channel.readerCandidateCountFor(message);
  const isReadByEveryone = message.readCount >= possibleReaderCount;
  const hasSoleReader = possibleReaderCount <= 1;
  const readByEveryoneLabel = hasSoleReader
    ? t('messaging.receipt_read')
    : t('messaging.receipt_read_by_all');
  const readByLabel = isReadByEveryone
    ? readByEveryoneLabel
    : t('messaging.receipt_read_by_some', {
        count: message.readCount,
        total: possibleReaderCount,
      });

  return (
    <>
      <Tooltip title={readByLabel}>
        <ButtonBase
          aria-expanded={readersAnchor !== null}
          aria-haspopup="dialog"
          aria-label={readByLabel}
          data-test-id="messaging-message-receipt-read"
          onClick={(event) => setReadersAnchor(event.currentTarget)}
          sx={{ borderRadius: theme.brandV2.borderRadius, color: 'inherit' }}
        >
          <DoneAllOutlined
            data-test-id="messaging-message-receipt-read-icon"
            sx={isReadByEveryone ? readByEveryoneIconSx : readIconSx}
          />
        </ButtonBase>
      </Tooltip>

      <Popover
        anchorEl={readersAnchor}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={() => setReadersAnchor(null)}
        open={readersAnchor !== null}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MessageReadersList channelId={channel.id} messageId={message.id} />
      </Popover>
    </>
  );
}
